import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { departmentAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import { RegistryHeader, Button as SharedButton } from "../../common/Shared";

const Department = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    department_Id: 0,
    department_Name: "",
    created_By: userId,
  });
  const [loading, setLoading] = useState(false);
  const [isDepartmentData, setIsDepartmentData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchDepartmentData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await departmentAPI.getAllDepartment(currentPage, pageSize);

      const newItems = res.data;

      if (currentPage === 1) {
        setIsDepartmentData(newItems);
      } else {
        setIsDepartmentData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching alloy data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchDepartmentData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isDepartmentData);
    } else {
      const filtered = isDepartmentData.filter((d) =>
        d.department_Name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, isDepartmentData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      department_Id: 0,
      department_Name: "",
      created_By: userId,
    });
  };

  const handleEdit = async (id) => {
    setLoading(true);
    try {
      const data = await departmentAPI.getById(id);
      if (data.data) {
        setFormData({
          department_Id: data.data.department_Id,
          department_Name: data.data.department_Name,
          Modify_By: userId,
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch alloy details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await departmentAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchDepartmentData();
    } catch (error) {
      notify("Failed to delete record", "error");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    setLoading(true);
    try {
      await departmentAPI.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      fetchDepartmentData();
      setFormData({
        department_Id: 0,
        department_Name: "",
        created_By: userId,
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    {
      name: "department_Name",
      label: "Department Name",
      type: "text",
      required: true,
      errorMessage: "Name is required",
    },
  ];

  return (
    <>
      <div className="flex flex-col h-full bg-white font-plus relative">
        {loading && <Loader centered />}
        <RegistryHeader title="Department Registry">
          <div className="relative w-full sm:w-80 group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-primary">
              <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              className="block w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-primary focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <MdClose size={18} />
              </button>
            )}
          </div>

          <SharedButton
            onClick={handleAdd}
            icon={HiOutlinePlus}
          >
            Add Department
          </SharedButton>
        </RegistryHeader>

        <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
          <div
            ref={tableScrollRef}
            className="relative overflow-auto border border-gray-200 bg-white flex-1"
          >
            <div className="min-w-max">
              <Table
                columns={[{ key: "department_Name", label: "Department Name" }]}
                data={filteredData}
                onEdit={(row) => handleEdit(row.department_Id)}
                onDelete={(row) => handleDelete(row.department_Id)}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Department" : "Add Department"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default Department;
